import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import * as api from '../../services/api';
import {
  type CandidateTranscription,
  type MarkingMetricsRow,
  type MarkingMetricsSettings,
  type User,
} from '../../types';

interface Props {
  row: MarkingMetricsRow;
  settings: MarkingMetricsSettings;
}

const YEAR_OPTIONS = [
  { id: '3', label: 'Year 3', values: [3] },
  { id: '4', label: 'Year 4', values: [4] },
  { id: '5', label: 'Year 5', values: [5] },
  { id: '6', label: 'Year 6', values: [6] },
  { id: 'ks3', label: 'KS3', values: [7, 8, 9] },
];
const CANDIDATES_PER_PAGE = 25;

const CandidateRow = memo(
  ({
    candidate,
    selected,
    onToggle,
  }: {
    candidate: CandidateTranscription;
    selected: boolean;
    onToggle: (id: string) => void;
  }) => (
    <Paper variant="outlined" sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Checkbox
        size="small"
        checked={selected}
        onChange={() => onToggle(candidate.transcriptionId)}
        inputProps={{ 'aria-label': `Select paper ${candidate.paperNumber}` }}
      />
      <Box>
        <Typography variant="subtitle2">
          Paper {candidate.paperNumber} · {candidate.batchName || 'No batch'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {candidate.isGroundTruth && <Chip color="primary" size="small" label="Ground truth" />}
          {candidate.hasAiMarks && <Chip color="success" size="small" label="AI marked" />}
          {candidate.yearGroup && <Chip size="small" label={`Year ${candidate.yearGroup}`} />}
          {candidate.purposeNames.map(name => (
            <Chip key={name} size="small" label={name} />
          ))}
          {candidate.assignedUserNames.map(name => (
            <Chip key={name} size="small" variant="outlined" label={`Assigned to ${name}`} />
          ))}
        </Box>
      </Box>
    </Paper>
  )
);
CandidateRow.displayName = 'CandidateRow';

const MarkingMetricsAssignmentPanel = ({ row, settings }: Props) => {
  const [preferGroundTruth, setPreferGroundTruth] = useState(true);
  const [purposeIds, setPurposeIds] = useState<string[]>(row.purposes.map(purpose => purpose.id));
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<CandidateTranscription[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [candidatePage, setCandidatePage] = useState(1);

  // Candidate availability is server-owned and must be refreshed when the operator changes triage filters.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setCandidatePage(1);
    setError(null);
    api
      .fetchCandidateTranscriptions(row.id, {
        purposeId: purposeIds,
        yearGroups: YEAR_OPTIONS.filter(option => yearOptions.includes(option.id)).flatMap(
          option => option.values
        ),
        preferGroundTruth,
      })
      .then(result => {
        if (!active) return;
        setCandidates(result);
        setSelectedIds(
          current =>
            new Set(
              Array.from(current).filter(id => result.some(item => item.transcriptionId === id))
            )
        );
      })
      .catch(() => {
        if (active) setError('Candidate transcriptions could not be loaded.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [preferGroundTruth, purposeIds, row.id, yearOptions]);

  // The assignable user list is shared server data and only needs loading when this criterion panel opens.
  useEffect(() => {
    let active = true;
    api
      .fetchUsers()
      .then(result => {
        if (active) setUsers(result);
      })
      .catch(() => {
        if (active) setError('Labellers could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, []);

  const neededCount = useMemo(() => {
    const threshold = settings.evidenceThresholds[row.granularity];
    return Math.max(
      1,
      (threshold.minComparedCases ?? 0) - row.comparedCount,
      (threshold.minDistinctTranscriptions ?? 0) - row.distinctComparedTranscriptions
    );
  }, [row, settings]);

  const selectableCandidates = useMemo(
    () =>
      selectedUserIds.length === 0
        ? []
        : candidates.filter(candidate =>
            selectedUserIds.every(userId => !candidate.assignedUserIds.includes(userId))
          ),
    [candidates, selectedUserIds]
  );

  const candidatePageCount = Math.ceil(candidates.length / CANDIDATES_PER_PAGE);
  const pagedCandidates = useMemo(
    () =>
      candidates.slice(
        (candidatePage - 1) * CANDIDATES_PER_PAGE,
        candidatePage * CANDIDATES_PER_PAGE
      ),
    [candidatePage, candidates]
  );

  const toggleCandidate = useCallback((id: string) => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const assign = async () => {
    if (selectedUserIds.length === 0 || selectedIds.size === 0) return;
    setAssigning(true);
    setError(null);
    try {
      const result = await api.bulkCreateAssignments(
        row.id,
        selectedUserIds,
        Array.from(selectedIds)
      );
      const selectedUsers = users.filter(user => selectedUserIds.includes(user.id));
      setCandidates(current =>
        current.map(candidate => {
          if (!selectedIds.has(candidate.transcriptionId)) return candidate;
          const newUsers = selectedUsers.filter(
            user => !candidate.assignedUserIds.includes(user.id)
          );
          return {
            ...candidate,
            assignedUserIds: [...candidate.assignedUserIds, ...newUsers.map(user => user.id)],
            assignedUserNames: [
              ...candidate.assignedUserNames,
              ...newUsers.map(user => `${user.firstName} ${user.lastName}`),
            ],
          };
        })
      );
      setSelectedIds(new Set());
      setSuccess(
        result.createdCount === 1
          ? 'Created 1 assignment.'
          : `Created ${result.createdCount} assignments across ${selectedUserIds.length} people.`
      );
    } catch {
      setError('The selected transcriptions could not be assigned.');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">Assign labelling</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Find unlabelled transcriptions that will strengthen this criterion&apos;s evaluation
        evidence.
      </Typography>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={preferGroundTruth}
              onChange={event => setPreferGroundTruth(event.target.checked)}
            />
          }
          label="Prefer ground-truth transcriptions"
        />
        <Box>
          <FormControl size="small" fullWidth>
            <InputLabel id="candidate-purpose-label">Writing purposes</InputLabel>
            <Select
              labelId="candidate-purpose-label"
              multiple
              value={purposeIds}
              label="Writing purposes"
              onChange={event => setPurposeIds(event.target.value as string[])}
              renderValue={selected =>
                selected.length === 0 ? 'All purposes' : `${selected.length} selected`
              }
            >
              {row.purposes.map(purpose => (
                <MenuItem key={purpose.id} value={purpose.id}>
                  <Checkbox checked={purposeIds.includes(purpose.id)} />
                  {purpose.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" disabled={purposeIds.length === 0} onClick={() => setPurposeIds([])}>
            Deselect all purposes
          </Button>
        </Box>
        <FormControl size="small">
          <InputLabel id="candidate-year-label">Student year groups</InputLabel>
          <Select
            labelId="candidate-year-label"
            multiple
            value={yearOptions}
            label="Student year groups"
            onChange={event => setYearOptions(event.target.value as string[])}
            renderValue={selected =>
              selected.length === 0
                ? 'All year groups'
                : YEAR_OPTIONS.filter(option => selected.includes(option.id))
                    .map(option => option.label)
                    .join(', ')
            }
          >
            {YEAR_OPTIONS.map(option => {
              return (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={yearOptions.includes(option.id)} />
                  {option.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel id="candidate-labeller-label">Assign to</InputLabel>
          <Select
            labelId="candidate-labeller-label"
            multiple
            value={selectedUserIds}
            label="Assign to"
            onChange={event => setSelectedUserIds(event.target.value as string[])}
            renderValue={selected => `${selected.length} selected`}
          >
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>
                <Checkbox checked={selectedUserIds.includes(user.id)} />
                {user.firstName} {user.lastName} ({user.userType})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          disabled={selectedUserIds.length === 0 || selectableCandidates.length === 0}
          onClick={() =>
            setSelectedIds(
              new Set(
                selectableCandidates
                  .slice(0, Math.min(neededCount, selectableCandidates.length))
                  .map(item => item.transcriptionId)
              )
            )
          }
        >
          {selectedUserIds.length === 0
            ? 'Choose assignees first'
            : `Select first ${Math.min(neededCount, selectableCandidates.length)} needed`}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : candidates.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No unlabelled transcriptions match these filters.
        </Alert>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {(candidatePage - 1) * CANDIDATES_PER_PAGE + 1}–
            {Math.min(candidatePage * CANDIDATES_PER_PAGE, candidates.length)} of{' '}
            {candidates.length} candidates
          </Typography>
          <Box sx={{ mt: 0.5, maxHeight: 360, overflowY: 'auto', display: 'grid', gap: 1 }}>
            {pagedCandidates.map(candidate => (
              <CandidateRow
                key={candidate.transcriptionId}
                candidate={candidate}
                selected={selectedIds.has(candidate.transcriptionId)}
                onToggle={toggleCandidate}
              />
            ))}
          </Box>
          {candidatePageCount > 1 && (
            <Pagination
              count={candidatePageCount}
              page={candidatePage}
              onChange={(_event, page) => setCandidatePage(page)}
              size="small"
              sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}
            />
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          disabled={selectedUserIds.length === 0 || selectedIds.size === 0 || assigning}
          onClick={assign}
        >
          Assign {selectedIds.size} transcriptions to {selectedUserIds.length} people
        </Button>
        <Typography variant="caption" color="text.secondary">
          Each selected transcription will be assigned to every selected person.
        </Typography>
      </Box>

      <Snackbar
        open={success !== null}
        autoHideDuration={5000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Paper>
  );
};

export default MarkingMetricsAssignmentPanel;
